import useSiteLeadStore from '@store/siteLeadStore';
import {
	isSiteVisitRecordingActive,
	useSiteVisitRecordingStore,
	type SiteVisitRecordingPhase,
} from '@store/siteVisitRecordingStore';
import {
	useSiteVisitRecordingUploadStore,
	type PendingSiteVisitRecordingUpload,
} from '@store/siteVisitRecordingUploadStore';
import {
	cleanupSiteVisitRecordingDataForSite,
	findInterruptedSiteVisitRecordingSession,
	findPendingUploadSiteVisitRecordingSession,
	persistSiteVisitRecordingSession,
	reassignSiteVisitRecordingSessionsSiteId,
	removeSiteVisitRecordingSession,
	type SiteVisitRecordingSessionRow,
	type SiteVisitRecordingSessionStatus,
} from '@services/siteVisitRecording/siteVisitRecordingPersistence';
import {
	isProvisionalSiteVisitRecordingSiteId,
	PROVISIONAL_NEW_SITE_RECORDING_ID,
} from '@services/siteVisitRecording/siteVisitRecordingConstants';
import {
	deleteRecordingChunksForSession,
	getRecordingChunksStats,
	mergeRecordingChunksFromIdb,
	persistRecordingChunk,
} from '@services/siteVisitRecording/siteVisitRecordingChunkStore';
import { assembleSiteVisitRecordingBlob, flushAndStopMediaRecorder } from '@services/siteVisitRecording/recordingMedia';
import { ToastType, useTosterStore } from '@store/toster';
import { getDatabase } from '@rxdb/config/databaseConfig';
import type { ProfileData } from '@services/hooks/profile';
import type { RxCollection } from 'rxdb';

type DbWithProfile = {
	profile: RxCollection<ProfileData>;
};

async function getProfileData(): Promise<{ bdeCode: string | null }> {
	try {
		const db = (await getDatabase()) as unknown as DbWithProfile;
		const profileDoc = await db.profile.findOne().exec();
		return {
			bdeCode: profileDoc?.mdicode_c ?? null,
		};
	} catch {
		return { bdeCode: null };
	}
}

function pickMimeType(): string {
	const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
	for (const c of candidates) {
		if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) {
			return c;
		}
	}
	return '';
}

function createTimerWorker(): Worker {
	return new Worker(new URL('../../workers/recordingTimer.worker.js', import.meta.url));
}

function formatDisplayTime(ms: number): string {
	const totalSec = Math.max(0, Math.floor(ms / 1000));
	const h = Math.floor(totalSec / 3600);
	const m = Math.floor((totalSec % 3600) / 60);
	const s = totalSec % 60;
	return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Resume timer from contiguous IndexedDB chunks only (not RxDB accumulatedMs, which can run ahead of chunks).
 * N saved chunks (indices 0..N-1) → timer 00:00:N, next chunk index N.
 */
export function computeResumeStateFromPersistedSession(contiguousChunkCount: number): {
	accumulatedMs: number;
	nextChunkIndex: number;
	displayTime: string;
} {
	const nextChunkIndex = contiguousChunkCount;
	const accumulatedMs = contiguousChunkCount * 1000;

	return {
		accumulatedMs,
		nextChunkIndex,
		displayTime: formatDisplayTime(accumulatedMs),
	};
}

function formatToastDuration(ms: number): string {
	const totalSec = Math.max(0, Math.floor(ms / 1000));
	const h = Math.floor(totalSec / 3600);
	const m = Math.floor((totalSec % 3600) / 60);
	const s = totalSec % 60;
	if (h > 0) {
		return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
	}
	return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function getStore(): ReturnType<typeof useSiteVisitRecordingStore.getState> {
	return useSiteVisitRecordingStore.getState();
}

const engineListeners = new Set<() => void>();

function notifyEngineListeners(): void {
	engineListeners.forEach((listener) => listener());
}

export function subscribeSiteVisitRecordingEngine(listener: () => void): () => void {
	engineListeners.add(listener);
	return (): void => {
		engineListeners.delete(listener);
	};
}

const sessionIdRef = { current: null as string | null };
const accumulatedMsRef = { current: 0 };
const runningSinceRef = { current: null as number | null };
const recordingStartedAtRef = { current: null as number | null };
const sessionActiveRef = { current: false };
const sessionSiteIdRef = { current: null as number | null };

const mediaRecorderRef = { current: null as MediaRecorder | null };
const mediaChunksRef = { current: [] as BlobPart[] };
const mediaStreamRef = { current: null as MediaStream | null };
const workerRef = { current: null as Worker | null };
const tickCountRef = { current: 0 };
const recordingChunksBytesRef = { current: 0 };
const chunkIndexRef = { current: 0 };
const mimeTypeRef = { current: 'audio/webm' };
const pendingChunkWritesRef = { current: [] as Promise<void>[] };
const playbackObjectUrlRef = { current: null as string | null };
const phaseRef = { current: 'idle' as SiteVisitRecordingPhase };
const finalizeInFlightRef = { current: null as Promise<FinalizeRecordingResult | null> | null };
const captureInterruptedRef = { current: false };
const stoppingCaptureRef = { current: false };
const interruptionTracksRef = { current: [] as MediaStreamTrack[] };

function setPhase(phase: SiteVisitRecordingPhase): void {
	phaseRef.current = phase;
	getStore().setPhase(phase);
	notifyEngineListeners();
}

function getElapsedMs(): number {
	const base = accumulatedMsRef.current;
	const run = runningSinceRef.current;
	if (!run) {
		return base;
	}
	return base + (Date.now() - run);
}

function resolveSessionSiteId(): number | null {
	return sessionSiteIdRef.current ?? getStore().sessionSiteId ?? useSiteLeadStore.getState().siteId ?? null;
}

function buildRow(status: SiteVisitRecordingSessionStatus): SiteVisitRecordingSessionRow | null {
	const id = sessionIdRef.current;
	if (!id) {
		return null;
	}
	const now = Date.now();
	const elapsedMs = status === 'recording' || status === 'paused' ? getElapsedMs() : accumulatedMsRef.current;
	return {
		id,
		siteId: resolveSessionSiteId(),
		status,
		accumulatedMs: elapsedMs,
		runningSince: runningSinceRef.current,
		recordingStartedAt: recordingStartedAtRef.current,
		completedAt: status === 'completed' ? now : null,
		finalDurationMs: status === 'completed' ? getElapsedMs() : null,
		chunkCount: chunkIndexRef.current,
		totalBytes: recordingChunksBytesRef.current,
		mimeType: mimeTypeRef.current ?? null,
		updatedAt: now,
		uploadOnStop: getStore().uploadOnStop,
	};
}

async function persistRow(status: SiteVisitRecordingSessionStatus): Promise<void> {
	const row = buildRow(status);
	if (!row) {
		return;
	}
	try {
		await persistSiteVisitRecordingSession(row);
	} catch {
		// offline / storage errors should not break recording UI
	}
}

function postWorkerSync(): void {
	const w = workerRef.current;
	if (!w) {
		return;
	}
	w.postMessage({
		type: 'SYNC',
		baseMs: accumulatedMsRef.current,
		runningSince: runningSinceRef.current,
	});
}

function stopWorker(): void {
	const w = workerRef.current;
	if (w) {
		w.postMessage({ type: 'STOP' });
		w.terminate();
		workerRef.current = null;
	}
}

async function pollStorageEstimate(): Promise<void> {
	try {
		if (!navigator.storage?.estimate) {
			return;
		}
		const est = await navigator.storage.estimate();
		getStore().setBrowserStorageUsed(est.usage ?? 0);
		getStore().setBrowserStorageQuota(est.quota ?? 0);
	} catch {
		// ignore
	}
}

function clearLastRecordingPlayback(): void {
	if (playbackObjectUrlRef.current) {
		URL.revokeObjectURL(playbackObjectUrlRef.current);
		playbackObjectUrlRef.current = null;
	}
	getStore().setLastRecordingPlaybackUrl(null);
}

function resetSessionRefs(): void {
	detachRecordingInterruptionListeners();
	captureInterruptedRef.current = false;
	sessionIdRef.current = null;
	accumulatedMsRef.current = 0;
	runningSinceRef.current = null;
	recordingStartedAtRef.current = null;
	mediaRecorderRef.current = null;
	mediaChunksRef.current = [];
	mediaStreamRef.current = null;
	workerRef.current = null;
	tickCountRef.current = 0;
	recordingChunksBytesRef.current = 0;
	chunkIndexRef.current = 0;
	mimeTypeRef.current = 'audio/webm';
	pendingChunkWritesRef.current = [];
	sessionActiveRef.current = false;
	sessionSiteIdRef.current = null;
	getStore().setRecordingBytesTotal(0);
	getStore().setIdbChunkCount(0);
	getStore().setSessionSiteId(null);
	notifyEngineListeners();
}

async function buildAudioBlobFromSession(clientSessionId: string, mimeSnapshot: string): Promise<Blob> {
	let audioBlob = await mergeRecordingChunksFromIdb(clientSessionId, mimeSnapshot);
	if (audioBlob.size === 0 && mediaChunksRef.current.length > 0) {
		audioBlob = assembleSiteVisitRecordingBlob([...mediaChunksRef.current], mimeSnapshot);
	}
	if (audioBlob.size === 0 && clientSessionId) {
		await new Promise<void>((resolve) => {
			window.setTimeout(resolve, 150);
		});
		audioBlob = await mergeRecordingChunksFromIdb(clientSessionId, mimeSnapshot);
		if (audioBlob.size === 0 && mediaChunksRef.current.length > 0) {
			audioBlob = assembleSiteVisitRecordingBlob([...mediaChunksRef.current], mimeSnapshot);
		}
	}
	return audioBlob;
}

export interface FinalizeRecordingResult {
	durationMs: number;
	clientSessionId: string;
	saved: boolean;
}

function pauseRecordingForExternalInterruption(): void {
	if (stoppingCaptureRef.current || phaseRef.current !== 'recording') {
		return;
	}
	void pauseSiteVisitRecording();
}

function onDocumentVisibilityChange(): void {
	if (document.visibilityState === 'hidden') {
		pauseRecordingForExternalInterruption();
	}
}

function onPageHidden(): void {
	pauseRecordingForExternalInterruption();
}

function onAudioTrackInterrupted(): void {
	pauseRecordingForExternalInterruption();
}

function onAudioTrackEnded(): void {
	if (stoppingCaptureRef.current) {
		return;
	}
	captureInterruptedRef.current = true;
	pauseRecordingForExternalInterruption();
}

function detachRecordingInterruptionListeners(): void {
	document.removeEventListener('visibilitychange', onDocumentVisibilityChange);
	document.removeEventListener('freeze', onPageHidden);
	window.removeEventListener('pagehide', onPageHidden);
	interruptionTracksRef.current.forEach((track) => {
		track.removeEventListener('mute', onAudioTrackInterrupted);
		track.removeEventListener('ended', onAudioTrackEnded);
	});
	interruptionTracksRef.current = [];
}

function attachRecordingInterruptionListeners(stream: MediaStream): void {
	detachRecordingInterruptionListeners();
	document.addEventListener('visibilitychange', onDocumentVisibilityChange);
	document.addEventListener('freeze', onPageHidden);
	window.addEventListener('pagehide', onPageHidden);
	const tracks = stream.getAudioTracks();
	interruptionTracksRef.current = tracks;
	tracks.forEach((track) => {
		track.addEventListener('mute', onAudioTrackInterrupted);
		track.addEventListener('ended', onAudioTrackEnded);
	});
}

async function restartMediaRecorderAfterInterruption(): Promise<void> {
	detachRecordingInterruptionListeners();
	mediaStreamRef.current?.getTracks().forEach((track) => {
		try {
			track.stop();
		} catch {
			// ignore
		}
	});

	const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
	mediaStreamRef.current = stream;
	const preferredMime = mimeTypeRef.current || pickMimeType();
	const recorder = preferredMime ? new MediaRecorder(stream, { mimeType: preferredMime }) : new MediaRecorder(stream);
	mediaRecorderRef.current = recorder;
	mimeTypeRef.current = recorder.mimeType ?? preferredMime ?? 'audio/webm';
	attachMediaRecorderDataHandler(recorder);
	recorder.start(1000);
	attachRecordingInterruptionListeners(stream);
	captureInterruptedRef.current = false;
}

async function stopRecordingCapture(): Promise<void> {
	stoppingCaptureRef.current = true;
	detachRecordingInterruptionListeners();
	stopWorker();
	const recorder = mediaRecorderRef.current;
	const stream = mediaStreamRef.current;
	try {
		await flushAndStopMediaRecorder(recorder);
		stream?.getTracks().forEach((t) => t.stop());
		await Promise.all(pendingChunkWritesRef.current);
		pendingChunkWritesRef.current = [];
	} finally {
		stoppingCaptureRef.current = false;
	}
}

async function syncRecordingChunkStatsFromIdb(clientSessionId: string): Promise<void> {
	try {
		const stats = await getRecordingChunksStats(clientSessionId);
		chunkIndexRef.current = stats.chunkCount;
		recordingChunksBytesRef.current = stats.totalBytes;
		getStore().setIdbChunkCount(stats.chunkCount);
		getStore().setRecordingBytesTotal(stats.totalBytes);
	} catch {
		// keep ref totals if IDB read fails
	}
}

function restoreLocalPlaybackPreview(audioBlob: Blob): void {
	clearLastRecordingPlayback();
	const objectUrl = URL.createObjectURL(audioBlob);
	playbackObjectUrlRef.current = objectUrl;
	getStore().setLastRecordingPlaybackUrl(objectUrl);
}

function showRecordingSavedLocallyToast(durationMs: number): void {
	useTosterStore
		.getState()
		.setToaster(
			true,
			ToastType.success,
			`Recording saved locally (Duration: ${formatToastDuration(durationMs)}). Upload runs on Submit.`,
			4500
		);
}

function showNoAudioCapturedToast(): void {
	useTosterStore
		.getState()
		.setToaster(
			true,
			ToastType.error,
			'Recording could not be saved (no audio captured). Please record again on the visit form.',
			5000
		);
}

async function tryUploadRecordingOnStop(pending: PendingSiteVisitRecordingUpload, showToast: boolean): Promise<void> {
	try {
		const { uploadPendingSiteVisitRecording } = await import(
			'@services/siteVisitRecording/uploadPendingSiteVisitRecording'
		);
		await uploadPendingSiteVisitRecording(pending, showToast);
		clearLastRecordingPlayback();
	} catch (err) {
		useSiteVisitRecordingUploadStore.getState().setPendingRecording(pending);
	}
}

function queueRecordingForSubmitUpload(
	pending: PendingSiteVisitRecordingUpload,
	showToast: boolean,
	durationMs: number
): void {
	useSiteVisitRecordingUploadStore.getState().setPendingRecording(pending);
	if (showToast) {
		showRecordingSavedLocallyToast(durationMs);
	}
}

function isPersistableRecording(clientSessionId: string, audioBlob: Blob, siteId: number | null): siteId is number {
	// Provisional (-1) is allowed for local IndexedDB queue until Task List binds a real site id.
	return Boolean(clientSessionId && audioBlob.size > 0 && siteId != null);
}

async function persistFinalizedRecordingAudio(params: {
	clientSessionId: string;
	siteId: number | null;
	durationMs: number;
	audioBlob: Blob;
	mimeSnapshot: string;
	uploadOnStop: boolean;
	showToast: boolean;
}): Promise<boolean> {
	const { clientSessionId, siteId, durationMs, audioBlob, mimeSnapshot, uploadOnStop, showToast } = params;

	if (!isPersistableRecording(clientSessionId, audioBlob, siteId)) {
		if (audioBlob.size === 0 && showToast) {
			showNoAudioCapturedToast();
		}
		return false;
	}

	const { bdeCode } = await getProfileData();
	const siteLeadState = useSiteLeadStore.getState();

	const pending: PendingSiteVisitRecordingUpload = {
		clientSessionId,
		siteId: siteId ?? 0, // Fallback to 0 if null, though isPersistableRecording ensures it's a number
		durationMs,
		audio: audioBlob,
		mimeType: mimeSnapshot,
		membershipno: siteLeadState.siteDetails?.membershipno ?? null,
		bdeCode,
		sfid: siteLeadState.visitSfid || null,
	};

	if (uploadOnStop) {
		await tryUploadRecordingOnStop(pending, showToast);
	} else {
		queueRecordingForSubmitUpload(pending, showToast, durationMs);
	}
	return true;
}

async function finalizeSiteVisitRecordingInternal(showToast: boolean): Promise<FinalizeRecordingResult | null> {
	if (!sessionActiveRef.current) {
		return null;
	}

	getStore().setIsSaving(true);
	const durationMs = getElapsedMs();
	const clientSessionId = sessionIdRef.current ?? '';
	const mimeSnapshot = mediaRecorderRef.current?.mimeType ?? mimeTypeRef.current ?? 'audio/webm';
	const siteId = resolveSessionSiteId();

	try {
		await stopRecordingCapture();

		const audioBlob = await buildAudioBlobFromSession(clientSessionId, mimeSnapshot);
		await syncRecordingChunkStatsFromIdb(clientSessionId);

		const uploadOnStop = getStore().uploadOnStop;
		if (audioBlob.size > 0 && !uploadOnStop) {
			restoreLocalPlaybackPreview(audioBlob);
		}

		const saved = await persistFinalizedRecordingAudio({
			clientSessionId,
			siteId,
			durationMs,
			audioBlob,
			mimeSnapshot,
			uploadOnStop,
			showToast,
		});

		await persistRow('completed');

		return { durationMs, clientSessionId, saved };
	} finally {
		sessionActiveRef.current = false;
		setPhase('idle');
		getStore().setUploadOnStop(false);
		getStore().setDisplayTime('00:00:00');
		getStore().setIsSaving(false);
		resetSessionRefs();
	}
}

export interface StartSiteVisitRecordingOptions {
	/** Upload audio to the backend when the user taps Stop (post–visit success page). */
	uploadOnStop?: boolean;
}

interface ActivateMediaRecorderSessionConfig {
	siteId: number;
	sessionId: string;
	uploadOnStop: boolean;
	contiguousChunkCount: number;
	totalBytes: number;
	accumulatedMs: number;
	nextChunkIndex: number;
	displayTime: string;
	recordingStartedAt: number | null;
	initialPhase: SiteVisitRecordingPhase;
	mimeTypeHint: string | null;
	clearMemoryChunks: boolean;
}

// We no longer upload chunks in the background.
// These functions are kept empty to satisfy existing references,
// but chunk uploading is now handled entirely on stop/submit via uploadPendingSiteVisitRecording.
export async function processChunkUploadQueue(): Promise<void> {
	return;
}

export async function waitForChunkUploadsToComplete(): Promise<void> {
	return;
}

function attachMediaRecorderDataHandler(recorder: MediaRecorder): void {
	recorder.ondataavailable = (e: BlobEvent): void => {
		if (!e.data || e.data.size <= 0) {
			return;
		}
		const sid = sessionIdRef.current;
		const chunkMime = mimeTypeRef.current;
		mediaChunksRef.current.push(e.data);
		recordingChunksBytesRef.current += e.data.size;
		getStore().setRecordingBytesTotal(recordingChunksBytesRef.current);

		if (!sid) {
			return;
		}
		const chunkIndex = chunkIndexRef.current;
		chunkIndexRef.current += 1;
		const writePromise = persistRecordingChunk(sid, chunkIndex, e.data, chunkMime)
			.then(() => {
				getStore().setIdbChunkCount(chunkIndexRef.current);
			})
			.catch(() => {
				// IndexedDB full / private mode — in-memory chunks remain as fallback
			});
		pendingChunkWritesRef.current.push(writePromise);
	};
}

function startRecordingTimerWorker(initialPhase: SiteVisitRecordingPhase, displayTime: string): void {
	stopWorker();
	const worker = createTimerWorker();
	workerRef.current = worker;
	worker.onmessage = (ev: MessageEvent): void => {
		if (worker !== workerRef.current) {
			return;
		}
		const d = ev.data as { type?: string; label?: string };
		if (d?.type === 'TICK' && typeof d.label === 'string') {
			getStore().setDisplayTime(d.label);
			tickCountRef.current += 1;
			if (tickCountRef.current % 5 === 0 && phaseRef.current === 'recording') {
				void persistRow('recording');
			}
		}
	};

	if (initialPhase === 'recording') {
		worker.postMessage({
			type: 'START',
			baseMs: accumulatedMsRef.current,
			runningSince: runningSinceRef.current,
		});
	} else {
		getStore().setDisplayTime(displayTime);
		worker.postMessage({
			type: 'SYNC',
			baseMs: accumulatedMsRef.current,
			runningSince: null,
		});
	}
}

async function activateMediaRecorderSession(config: ActivateMediaRecorderSessionConfig): Promise<void> {
	getStore().setUploadOnStop(config.uploadOnStop);
	sessionIdRef.current = config.sessionId;
	sessionSiteIdRef.current = config.siteId;
	getStore().setSessionSiteId(config.siteId);
	chunkIndexRef.current = config.nextChunkIndex;
	recordingChunksBytesRef.current = config.totalBytes;
	accumulatedMsRef.current = config.accumulatedMs;
	getStore().setIdbChunkCount(config.contiguousChunkCount);
	getStore().setRecordingBytesTotal(config.totalBytes);
	if (config.initialPhase === 'paused') {
		getStore().setDisplayTime(config.displayTime);
	}
	recordingStartedAtRef.current = config.recordingStartedAt ?? Date.now();
	pendingChunkWritesRef.current = [];
	tickCountRef.current = 0;

	if (config.clearMemoryChunks) {
		mediaChunksRef.current = [];
	}

	const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
	mediaStreamRef.current = stream;
	const preferredMime = config.mimeTypeHint ?? pickMimeType();
	const recorder = preferredMime ? new MediaRecorder(stream, { mimeType: preferredMime }) : new MediaRecorder(stream);
	mediaRecorderRef.current = recorder;
	mimeTypeRef.current = recorder.mimeType ?? preferredMime ?? 'audio/webm';

	attachMediaRecorderDataHandler(recorder);
	recorder.start(1000);
	attachRecordingInterruptionListeners(stream);

	const now = Date.now();
	if (config.initialPhase === 'recording') {
		runningSinceRef.current = now;
	} else {
		runningSinceRef.current = null;
		if (recorder.state === 'recording') {
			recorder.pause();
		}
	}

	sessionActiveRef.current = true;
	setPhase(config.initialPhase);
	notifyEngineListeners();
	startRecordingTimerWorker(config.initialPhase, config.displayTime);

	await persistRow(config.initialPhase === 'paused' ? 'paused' : 'recording');
	void pollStorageEstimate();
}

function handleMicrophoneActivationFailure(): void {
	detachRecordingInterruptionListeners();
	stopWorker();
	const r = mediaRecorderRef.current;
	if (r && r.state !== 'inactive') {
		try {
			r.stop();
		} catch {
			// ignore
		}
	}
	mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
	resetSessionRefs();
	setPhase('idle');
	getStore().setDisplayTime('00:00:00');
}

/**
 * Form page only: resume an interrupted session after reload using existing IndexedDB chunks + session id.
 */
export async function recoverSiteVisitRecordingSession(siteId: number): Promise<boolean> {
	const { phase } = getStore();
	if (phase !== 'idle' || sessionActiveRef.current) {
		return false;
	}

	const row = await findInterruptedSiteVisitRecordingSession(siteId);
	if (!row) {
		return false;
	}

	// Drop any other sessions for this site (e.g. completed recording from a prior visit).
	await cleanupSiteVisitRecordingDataForSite(siteId, row.id);

	let stats: { chunkCount: number; totalBytes: number };
	try {
		stats = await getRecordingChunksStats(row.id);
	} catch {
		return false;
	}

	if (stats.chunkCount <= 0 || stats.totalBytes <= 0) {
		await cleanupSiteVisitRecordingDataForSite(siteId);
		return false;
	}

	const resume = computeResumeStateFromPersistedSession(stats.chunkCount);
	const initialPhase: SiteVisitRecordingPhase = row.status === 'paused' ? 'paused' : 'recording';

	try {
		await activateMediaRecorderSession({
			siteId,
			sessionId: row.id,
			uploadOnStop: row.uploadOnStop,
			contiguousChunkCount: stats.chunkCount,
			totalBytes: stats.totalBytes,
			accumulatedMs: resume.accumulatedMs,
			nextChunkIndex: resume.nextChunkIndex,
			displayTime: resume.displayTime,
			recordingStartedAt: row.recordingStartedAt,
			initialPhase,
			mimeTypeHint: row.mimeType,
			clearMemoryChunks: true,
		});

		// We no longer queue chunks for background upload on resume
		useTosterStore
			.getState()
			.setToaster(true, ToastType.success, `Recording resumed from ${resume.displayTime}.`, 4000);
		return true;
	} catch {
		handleMicrophoneActivationFailure();
		useTosterStore
			.getState()
			.setToaster(true, ToastType.error, 'Microphone access is required to resume recording.', 4000);
		return false;
	}
}

/**
 * Form/draft page only: restore a finished-but-not-yet-uploaded recording (e.g. Submit failed,
 * or the tab was closed/reloaded before the upload completed) into the pending-upload store so it
 * is retried instead of being silently discarded.
 *
 * Does not touch IndexedDB — the recording (chunks + session row) stays exactly where it is until
 * either the upload succeeds or the user explicitly starts a new recording from scratch, which is
 * the only place that clears prior data (see `cleanupSiteVisitRecordingDataForSite` below).
 */
export async function recoverPendingSiteVisitRecordingUpload(siteId: number): Promise<boolean> {
	if (sessionActiveRef.current || getStore().phase !== 'idle') {
		return false;
	}
	if (useSiteVisitRecordingUploadStore.getState().pending) {
		return false;
	}

	const row = await findPendingUploadSiteVisitRecordingSession(siteId);
	if (!row) {
		return false;
	}

	const mimeSnapshot = row.mimeType || 'audio/webm';
	let audioBlob: Blob;
	try {
		audioBlob = await mergeRecordingChunksFromIdb(row.id, mimeSnapshot);
	} catch {
		return false;
	}
	if (audioBlob.size === 0) {
		return false;
	}

	const { bdeCode } = await getProfileData();
	const siteLeadState = useSiteLeadStore.getState();
	const pending: PendingSiteVisitRecordingUpload = {
		clientSessionId: row.id,
		siteId,
		durationMs: row.finalDurationMs ?? row.accumulatedMs,
		audio: audioBlob,
		mimeType: mimeSnapshot,
		membershipno: siteLeadState.siteDetails?.membershipno ?? null,
		bdeCode,
		sfid: siteLeadState.visitSfid || null,
	};

	useSiteVisitRecordingUploadStore.getState().setPendingRecording(pending);
	restoreSiteVisitRecordingPlaybackFromBlob(audioBlob);
	useTosterStore
		.getState()
		.setToaster(true, ToastType.success, 'Previous recording restored. It will be uploaded on Submit.', 4500);
	return true;
}

export async function finalizeSiteVisitRecording(showToast: boolean): Promise<FinalizeRecordingResult | null> {
	if (finalizeInFlightRef.current) {
		return finalizeInFlightRef.current;
	}
	if (!sessionActiveRef.current) {
		return null;
	}

	const promise = finalizeSiteVisitRecordingInternal(showToast);

	const wrappedPromise = promise.then(
		(res) => {
			finalizeInFlightRef.current = null;
			return res;
		},
		(err) => {
			finalizeInFlightRef.current = null;
			throw err;
		}
	);

	finalizeInFlightRef.current = wrappedPromise;

	return wrappedPromise;
}

async function uploadPendingVisitFormRecordingIfAny(): Promise<void> {
	const pending = useSiteVisitRecordingUploadStore.getState().pending;
	if (!pending) {
		return;
	}
	try {
		const { uploadPendingSiteVisitRecording } = await import(
			'@services/siteVisitRecording/uploadPendingSiteVisitRecording'
		);
		await uploadPendingSiteVisitRecording(pending, true);
		useSiteVisitRecordingUploadStore.getState().clearPendingRecording();
	} catch {
		// leave pending for a later retry (error toast already shown)
	}
}

export async function startSiteVisitRecording(siteId: number, options?: StartSiteVisitRecordingOptions): Promise<void> {
	const { phase } = getStore();
	if (phase !== 'idle' || sessionActiveRef.current) {
		return;
	}

	clearLastRecordingPlayback();
	useSiteVisitRecordingUploadStore.getState().clearPendingRecording();

	// Fresh recording: remove prior sessions/chunks for this site (form, draft, or BDE feedback).
	await cleanupSiteVisitRecordingDataForSite(siteId);
	if (isProvisionalSiteVisitRecordingSiteId(siteId)) {
		await cleanupSiteVisitRecordingDataForSite(PROVISIONAL_NEW_SITE_RECORDING_ID);
	}

	const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `rec-${Date.now()}`;

	try {
		await activateMediaRecorderSession({
			siteId,
			sessionId: id,
			uploadOnStop: options?.uploadOnStop ?? false,
			contiguousChunkCount: 0,
			totalBytes: 0,
			accumulatedMs: 0,
			nextChunkIndex: 0,
			displayTime: '00:00:00',
			recordingStartedAt: Date.now(),
			initialPhase: 'recording',
			mimeTypeHint: null,
			clearMemoryChunks: true,
		});
	} catch {
		useTosterStore
			.getState()
			.setToaster(true, ToastType.error, 'Microphone access is required to start recording.', 4000);
		handleMicrophoneActivationFailure();
	}
}

/**
 * After Contractor 360 "+" creates a site lead on Task List, reassign the active/pending
 * recording from the provisional id to the real site id so Submit can upload to GCP.
 */
export async function bindSiteVisitRecordingSiteId(siteId: number): Promise<void> {
	if (isProvisionalSiteVisitRecordingSiteId(siteId)) {
		return;
	}

	const previousSiteId = sessionSiteIdRef.current ?? getStore().sessionSiteId;
	sessionSiteIdRef.current = siteId;
	getStore().setSessionSiteId(siteId);

	if (sessionActiveRef.current && sessionIdRef.current) {
		const status: SiteVisitRecordingSessionStatus = phaseRef.current === 'paused' ? 'paused' : 'recording';
		await persistRow(status);
	}

	if (previousSiteId != null && isProvisionalSiteVisitRecordingSiteId(previousSiteId)) {
		await reassignSiteVisitRecordingSessionsSiteId(previousSiteId, siteId);
	} else if (previousSiteId == null) {
		await reassignSiteVisitRecordingSessionsSiteId(PROVISIONAL_NEW_SITE_RECORDING_ID, siteId);
	}

	const pending = useSiteVisitRecordingUploadStore.getState().pending;
	if (pending && isProvisionalSiteVisitRecordingSiteId(pending.siteId)) {
		useSiteVisitRecordingUploadStore.getState().setPendingRecording({ ...pending, siteId });
	}
}

/** Post–visit BDE Feedback: always starts a fresh session (0 chunks / timer) and uploads on Save. */
export async function startBdeFeedbackRecording(siteId: number): Promise<void> {
	if (getStore().uploadOnStop && sessionActiveRef.current) {
		return;
	}

	if (sessionActiveRef.current || isSiteVisitRecordingActive(getStore().phase)) {
		const wasBdeFeedback = getStore().uploadOnStop;
		await finalizeSiteVisitRecording(wasBdeFeedback);
		if (!wasBdeFeedback) {
			await uploadPendingVisitFormRecordingIfAny();
		}
	}

	if (getStore().phase !== 'idle' || sessionActiveRef.current) {
		return;
	}

	clearLastRecordingPlayback();
	await cleanupSiteVisitRecordingDataForSite(siteId);

	const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `rec-${Date.now()}`;

	try {
		await activateMediaRecorderSession({
			siteId,
			sessionId: id,
			uploadOnStop: true,
			contiguousChunkCount: 0,
			totalBytes: 0,
			accumulatedMs: 0,
			nextChunkIndex: 0,
			displayTime: '00:00:00',
			recordingStartedAt: Date.now(),
			initialPhase: 'recording',
			mimeTypeHint: null,
			clearMemoryChunks: true,
		});
	} catch {
		useTosterStore
			.getState()
			.setToaster(true, ToastType.error, 'Microphone access is required to start recording.', 4000);
		handleMicrophoneActivationFailure();
	}
}

export async function pauseSiteVisitRecording(): Promise<void> {
	if (getStore().phase !== 'recording') {
		return;
	}
	const recorder = mediaRecorderRef.current;
	const run = runningSinceRef.current;
	if (run) {
		accumulatedMsRef.current += Date.now() - run;
		runningSinceRef.current = null;
	}
	getStore().setDisplayTime(formatDisplayTime(accumulatedMsRef.current));
	postWorkerSync();
	if (recorder && recorder.state === 'recording') {
		try {
			recorder.pause();
		} catch {
			// ignore
		}
	}
	setPhase('paused');
	await persistRow('paused');
}

export async function resumeSiteVisitRecording(): Promise<void> {
	if (getStore().phase !== 'paused') {
		return;
	}
	const recorder = mediaRecorderRef.current;
	const needsRestart = captureInterruptedRef.current || !recorder || recorder.state === 'inactive';
	const now = Date.now();
	runningSinceRef.current = now;
	postWorkerSync();
	if (needsRestart) {
		try {
			await restartMediaRecorderAfterInterruption();
		} catch {
			runningSinceRef.current = null;
			postWorkerSync();
			useTosterStore
				.getState()
				.setToaster(true, ToastType.error, 'Microphone access is required to resume recording.', 4000);
			return;
		}
	} else if (recorder.state === 'paused') {
		try {
			recorder.resume();
		} catch {
			// ignore
		}
	}
	setPhase('recording');
	await persistRow('recording');
}

export async function stopSiteVisitRecording(): Promise<FinalizeRecordingResult | null> {
	return finalizeSiteVisitRecording(true);
}

export function isSiteVisitRecordingSessionActive(): boolean {
	return sessionActiveRef.current || isSiteVisitRecordingActive(getStore().phase);
}

/**
 * Drop any in-progress or stopped-but-not-uploaded visit recording without calling the upload API.
 * Used when the next site/visit is not eligible (consent false).
 * After Stop, session refs are cleared — also wipe pending + IndexedDB via the pending store.
 */
export async function discardActiveSiteVisitRecording(): Promise<void> {
	const pending = useSiteVisitRecordingUploadStore.getState().pending;
	const clientSessionId = sessionIdRef.current ?? pending?.clientSessionId ?? null;
	const siteId = resolveSessionSiteId() ?? pending?.siteId ?? null;

	if (sessionActiveRef.current) {
		try {
			await stopRecordingCapture();
		} catch {
			// still reset UI/session even if stop fails
		}
	}

	useSiteVisitRecordingUploadStore.getState().clearPendingRecording();
	clearLastRecordingPlayback();

	try {
		if (clientSessionId) {
			await deleteRecordingChunksForSession(clientSessionId);
			await removeSiteVisitRecordingSession(clientSessionId);
		}
		if (siteId != null) {
			await cleanupSiteVisitRecordingDataForSite(siteId);
		}
	} catch {
		// ignore local cleanup errors
	}

	sessionActiveRef.current = false;
	setPhase('idle');
	getStore().setUploadOnStop(false);
	getStore().setDisplayTime('00:00:00');
	getStore().setIsSaving(false);
	resetSessionRefs();
}

export function getSiteVisitRecordingPhase(): SiteVisitRecordingPhase {
	return getStore().phase;
}

/** Elapsed recording time in ms (active or paused session). Returns 0 when idle. */
export function getSiteVisitRecordingElapsedMs(): number {
	if (!sessionActiveRef.current) {
		return 0;
	}
	return getElapsedMs();
}

export function disposeSiteVisitRecordingPlayback(): void {
	clearLastRecordingPlayback();
}

/** Restores local playback URL after recovering a merged recording from IndexedDB. */
export function restoreSiteVisitRecordingPlaybackFromBlob(blob: Blob): void {
	clearLastRecordingPlayback();
	const url = URL.createObjectURL(blob);
	playbackObjectUrlRef.current = url;
	getStore().setLastRecordingPlaybackUrl(url);
}
